/**
 * UserFlow Script - AI Assistant Playbook
 *
 * This script defines the exact workflow the AI assistant should follow
 * when guiding new users through post-onboarding setup.
 *
 * The AI reads this script and follows the defined paths, conditions, and prompts.
 * No deviation from this script unless explicitly modified.
 *
 * Structure:
 * - Stages: Major milestones (e.g., "Role Creation", "User Invitation")
 * - Flows: Specific workflows within each stage
 * - Conditions: Decision points based on data/user action
 * - Prompts: Exact AI messages to send
 * - Forms: Fields user must fill
 */

// ============================================
// TYPES & INTERFACES
// ============================================

export type StageId = 'initial_check' | 'role_creation' | 'user_invitation' | 'setup_complete';
export type FlowId = string;
export type ActionType = 'check' | 'guide' | 'form' | 'confirm' | 'navigate' | 'wait' | 'ai_form';
export type RoleType = 'admin' | 'manager' | 'viewer' | 'contributor';

export interface UserFlowScript {
  version: string;
  lastUpdated: string;
  description: string;
  stages: Stage[];
  flows: Record<FlowId, Flow>;
  forms: Record<string, FormDefinition>;
  aiPrompts: Record<string, AiPrompt>;
}

export interface Stage {
  id: StageId;
  name: string;
  description: string;
  priority: 'critical' | 'important' | 'optional';
  isRequired: boolean;
  completionCriteria: CompletionCriteria;
  nextStages: StageId[];
  estimatedTime: string; // e.g., "5-10 minutes"
}

export interface CompletionCriteria {
  type: 'count' | 'boolean' | 'field';
  entity: string; // 'roles', 'users', 'team_members', etc.
  condition: string; // e.g., "atLeastOne", "all", "filled"
  value?: number | string;
}

export interface Flow {
  id: FlowId;
  name: string;
  description: string;
  stageId: StageId;
  steps: Step[];
  exitConditions: ExitCondition[];
  fallbackAction?: string; // What to do if user cancels/gets stuck
}

export interface Step {
  order: number;
  action: ActionType;
  id: string;
  title: string;
  description: string;

  // For 'check' action
  check?: {
    endpoint: string;
    condition: string; // e.g., "count > 0", "isTrue", "isEmpty"
    branches: {
      true: Step | string; // Next step or step ID
      false: Step | string;
    };
  };

  // For 'guide' action (AI message)
  guide?: {
    promptId: string;
    format: 'text' | 'markdown' | 'list';
    showOptions?: boolean;
  };

  // For 'form' action
  form?: {
    formId: string;
    fields: string[];
    endpoint: string;
    method: 'POST' | 'PUT';
  };

  // For 'navigate' action
  navigate?: {
    path: string;
    label: string;
    description?: string;
    uiSteps?: UIStep[]; // Steps to navigate to the correct tab/section
  };

  // For 'confirm' action
  confirm?: {
    promptId: string;
    options: ConfirmOption[];
  };

  // For 'ai_form' action (conversational form filling with AI)
  ai_form?: {
    metadataKey: string;  // Key to look up in AiFormRegistry
    endpoint: string;     // API endpoint to submit form data
    method: 'POST' | 'PUT';
  };

  nextStep?: string | Step; // ID of next step or inline step
  conditions?: Condition[]; // Conditional next steps
  uiInteractions?: UIInteraction[]; // UI element interactions for this step
}

export interface Condition {
  if: string; // Condition expression
  then: string; // Next step ID
}

export interface UIStep {
  action: 'click' | 'fill' | 'select' | 'wait' | 'scroll';
  selector?: string; // CSS selector or aria-label
  value?: string; // For fill/select actions
  description: string;
  waitMs?: number; // For wait action
}

export interface UIInteraction {
  element: string; // Element identifier (selector, aria-label, data-testid)
  action: 'click' | 'fill' | 'select' | 'verify';
  value?: string; // Value to fill or select
  expectedResult?: string; // What should happen after interaction
}

export interface ExitCondition {
  type: 'completed' | 'cancelled' | 'timeout' | 'error';
  action: string; // What to do next
}

export interface FormDefinition {
  id: string;
  name: string;
  description: string;
  fields: FormField[];
  validationRules: ValidationRule[];
  submitButton: {
    text: string;
    successMessage: string;
  };
}

export interface FormField {
  name: string;
  type: 'text' | 'select' | 'checkbox' | 'textarea' | 'email';
  label: string;
  placeholder?: string;
  required: boolean;
  helpText?: string;
  example?: string;
  validation?: {
    pattern?: string;
    minLength?: number;
    maxLength?: number;
  };
  uiSelector?: string; // CSS selector or aria-label for this field in the UI
  inputSelector?: string; // Specific selector for the input element
  fillInstructions?: string; // AI instruction on how to fill this field
}

export interface ValidationRule {
  field: string;
  rule: string;
  errorMessage: string;
}

export interface AiPrompt {
  id: string;
  context: string; // When to show this prompt
  variants: string[]; // Multiple phrasings for naturalness
  followUp?: {
    text: string;
    expectedResponses: string[];
  };
}

export interface ConfirmOption {
  text: string;
  value: string;
  description?: string;
  nextStep: string; // Step ID to go to
}

// ============================================
// AI FORM INTERFACES (Conversational Form Filling)
// ============================================

export interface AiFormFieldMeta {
  name: string;
  label: string;
  type: 'text' | 'email' | 'textarea' | 'select' | 'multi-select' | 'checkbox';
  required: boolean;
  helpText?: string;
  example?: string;
  options?: { value: string; label: string }[];         // Static options
  dynamicOptionsKey?: 'roles' | 'departments' | 'locations'; // Resolved at runtime via API
}

export interface AiFormMetadata {
  formId: string;
  formName: string;
  endpoint: string;
  method: 'POST' | 'PUT';
  fields: AiFormFieldMeta[];
}

export interface AiFormTurn {
  role: 'assistant' | 'user';
  content: string;
}

export interface AiFormSession {
  formId: string;
  metadata: AiFormMetadata;
  resolvedOptions: Record<string, { value: string; label: string }[]>;
  turns: AiFormTurn[];
  collectedData: Record<string, any>;
  endpoint: string;
  method: 'POST' | 'PUT';
}

// ============================================
// USERFLOW SCRIPT DEFINITION
export class UserFlowHelper {
  /**
   * Get the current stage for a user based on their completion status
   */
  static getCurrentStage(
    rolesCount: number,
    usersCount: number
  ): StageId {
    if (rolesCount === 0) return 'role_creation';
    if (usersCount === 0) return 'user_invitation';
    return 'setup_complete';
  }

  /**
   * Get the flow to execute based on current stage
   */
  static getFlowForStage(stage: StageId, flows: Record<FlowId, Flow>): Flow | null {
    switch (stage) {
      case 'initial_check':
        return flows['initial_assessment'] || null;
      case 'role_creation':
        return flows['create_role_flow'] || null;
      case 'user_invitation':
        return flows['invite_user_flow'] || null;
      case 'setup_complete':
        return flows['setup_complete_flow'] || null;
      default:
        return null;
    }
  }

  /**
   * Get a random variant of a prompt for naturalness
   */
  static getPromptVariant(prompt: AiPrompt): string {
    return prompt.variants[Math.floor(Math.random() * prompt.variants.length)];
  }

  /**
   * Check if a stage is complete based on its criteria
   */
  static isStageComplete(
    criteria: CompletionCriteria,
    dataCount: number,
    dataField?: any
  ): boolean {
    switch (criteria.type) {
      case 'count':
        if (criteria.condition === 'atLeastOne') {
          return dataCount >= 1;
        }
        if (criteria.condition === 'atLeastTwo') {
          return dataCount >= 2;
        }
        if (criteria.condition === 'all') {
          return dataCount > 0;
        }
        break;
      case 'boolean':
        return criteria.value === true;
      case 'field':
        return dataField !== null && dataField !== undefined && dataField !== '';
    }
    return false;
  }
}

// ============================================
// NOTE: Flow definitions have moved to scripts/userflows/
// This file now only contains types and utilities
// ============================================
/**
 * SCRIPT USAGE GUIDE FOR AI ASSISTANT
 *
 * This script defines the EXACT workflow that the AI assistant should follow.
 * The AI must NOT deviate from these steps unless the user explicitly requests otherwise.
 *
 * WORKFLOW EXECUTION:
 *
 * 1. INITIAL CHECK (initial_assessment flow)
 *    - When user logs in, run the 'initial_assessment' flow
 *    - Call API endpoints to check: roles count, invitations count
 *    - Based on results, determine next stage
 *    - Possible outcomes:
 *      a) No roles → Go to 'create_role_flow'
 *      b) Roles exist but no invites → Go to 'invite_user_flow'
 *      c) Both exist → Go to 'setup_complete_flow'
 *
 * 2. NAVIGATION & UI INTERACTION
 *    - When a 'navigate' action has 'uiSteps', follow them precisely:
 *      • Use selectors from 'uiSelector' or 'selector' field
 *      • For tabs: Look for TabsTrigger with value="roles"
 *      • For buttons: Look for text "Create Role" or aria-label
 *      • For form fields: Use inputSelector to locate exact input element
 *    - Wait between navigation steps for DOM to update
 *    - Verify user is on correct page/tab before proceeding
 *
 * 3. FORM FILLING
 *    - When 'form' action is executed:
 *      • Get the form definition from forms[formId]
 *      • Follow the order of fields exactly as listed
 *      • Use 'fillInstructions' to guide user on what to enter
 *      • For each field:
 *        - Find the input using 'inputSelector'
 *        - Send message with 'fillInstructions'
 *        - Wait for user to fill the field
 *        - Provide error message if validation fails
 *      • After all fields filled, submit via submit button
 *    - Use 'validationRules' to validate user input
 *    - If validation fails, show error and ask user to correct
 *
 * 4. PROMPTS & MESSAGES
 *    - Use aiPrompts[promptId] to get AI message templates
 *    - Randomly select one variant for naturalness
 *    - Adapt the message tone to user context
 *    - Always explain WHY (not just WHAT)
 *
 * 5. COMPLETION CHECKS
 *    - After each stage, verify completion criteria:
 *      • role_creation: Check "roles count >= 1"
 *      • user_invitation: Check "invitations count >= 1"
 *      • setup_complete: Check both conditions are met
 *    - If user cancels or gets stuck, use 'fallbackAction'
 *
 * ============================================
 * KEY PRINCIPLES
 * ============================================
 *
 * ✓ MUST FOLLOW: Don't skip or reorder steps
 * ✓ MUST VERIFY: Check that UI elements loaded before interacting
 * ✓ MUST GUIDE: Explain each form field before asking user to fill
 * ✓ MUST VALIDATE: Ensure all required fields are filled correctly
 * ✓ MUST CONFIRM: Get user confirmation before major actions
 *
 * ✗ DON'T DEVIATE: Unless user explicitly requests
 * ✗ DON'T SKIP FORMS: Every form field must be addressed
 * ✗ DON'T ASSUME: Always verify user is ready before proceeding
 *
 * ============================================
 * FORM FIELD MAPPING
 * ============================================
 *
 * CREATE ROLE FORM:
 *   1. roleName (text) → Role Name field
 *      Selector: dialog input[type="text"]:first-of-type
 *      Example: "ESG Data Manager"
 *
 *   2. description (textarea) → Description field
 *      Selector: dialog textarea
 *      Example: "Responsible for collecting environmental data"
 *
 *   3. permissions (checkbox) → Permissions checkboxes
 *      Selector: dialog div:has(> [role="checkbox"])
 *      Action: Check relevant permission boxes
 *
 * INVITE USER FORM:
 *   1. email (email) → Email Address field
 *   2. firstName (text) → First Name field
 *   3. lastName (text) → Last Name field
 *   4. jobTitle (text) → Job Title field
 *   5. role (select) → Assign Role dropdown
 *      Note: Options are dynamically loaded from created roles
 *
 * ============================================
 * ERROR HANDLING
 * ============================================
 *
 * If API call fails:
 *   - Show error message to user
 *   - Ask if they want to retry
 *   - Don't proceed until issue is resolved
 *
 * If user closes modal:
 *   - Show fallback action message
 *   - Ask if they want to try again
 *   - Don't force them but explain consequences
 *
 * If validation fails:
 *   - Show specific error message
 *   - Point to the field that failed
 *   - Ask user to correct and try again
 *
 * ============================================
 * CHECKING USER STATE ON FIRST LOGIN
 * ============================================
 *
 * STEP 1: Check if User has Created Roles
 * ========================================
 * API: GET /roles (automatically filtered by tenantId)
 *
 * Code:
 * ```
 * const response = await api.get('/roles');
 * const roles = response.data;  // Array of Role objects
 * const hasRoles = roles && roles.length > 0;
 * ```
 *
 * What it returns:
 * - Empty array [] → User has NOT created any roles
 * - Array with items [{id, name, description, permissions, ...}] → User HAS created roles
 *
 * Decision:
 * - If hasRoles = FALSE → Go to 'create_role_flow'
 * - If hasRoles = TRUE → Check next condition
 *
 * ============================================
 * STEP 2: Check if User has Invited Any Users
 * =============================================
 * API: GET /users (returns all users in tenant) + Client-side filter
 *
 * Code:
 * ```
 * const response = await api.get('/users');
 * const allUsers = response.data;  // Array of User objects
 * const currentUser = await api.get('/auth/profile');
 * const currentUserId = currentUser.data.id;
 *
 * // Filter to get only users invited by current user
 * const myInvitedUsers = allUsers.filter(user => user.invitedBy === currentUserId);
 * const hasInvitedUsers = myInvitedUsers && myInvitedUsers.length > 0;
 * ```
 *
 * What it returns:
 * - Empty array [] → User has NOT invited anyone
 * - Array with items → User HAS invited team members
 *
 * Each user object has:
 * {
 *   id: string,
 *   email: string,
 *   firstName: string,
 *   lastName: string,
 *   status: 'active' | 'invited' | 'inactive',  // 'invited' = pending acceptance
 *   invitedBy: string,  // UUID of who invited them
 *   role: string,
 *   createdAt: string,
 *   ... other fields
 * }
 *
 * Decision:
 * - If hasInvitedUsers = FALSE → Go to 'invite_user_flow'
 * - If hasInvitedUsers = TRUE → Go to 'setup_complete_flow'
 *
 * ============================================
 * COMPLETE STATE CHECK LOGIC
 * ============================================
 *
 * // Step 1: Get current user
 * const currentUser = await api.get('/auth/profile').data;
 *
 * // Step 2: Get all roles
 * const roles = await api.get('/roles').data;
 * const rolesCount = roles?.length || 0;
 *
 * // Step 3: Get all users and filter
 * const allUsers = await api.get('/users').data;
 * const myInvites = allUsers.filter(u => u.invitedBy === currentUser.id);
 * const invitesCount = myInvites?.length || 0;
 *
 * // Step 4: Determine stage
 * if (rolesCount === 0) {
 *   // No roles created → Stage: role_creation
 *   return 'role_creation';
 * } else if (invitesCount === 0) {
 *   // Roles exist but no invites → Stage: user_invitation
 *   return 'user_invitation';
 * } else {
 *   // Both exist → Stage: setup_complete
 *   return 'setup_complete';
 * }
 *
 * ============================================
 * IMPORTANT NOTES
 * ============================================
 *
 * 1. **Automatic Tenant Filtering**
 *    - Both /roles and /users endpoints automatically filter by tenantId
 *    - No need to pass tenantId in query - it's extracted from JWT
 *    - The JWT contains: user.tenantId, user.id, user.email, etc.
 *
 * 2. **Client-side User Filtering**
 *    - /users returns ALL users in the tenant
 *    - We filter client-side by: user.invitedBy === currentUser.id
 *    - This shows only users THIS user invited
 *
 * 3. **User Status Values**
 *    - 'invited' → User invitation sent, pending acceptance
 *    - 'active' → User has accepted and is active
 *    - 'inactive' → User has been deactivated
 *    - Any count > 0 means they've invited someone
 *
 * 4. **When to Run This Check**
 *    - On first login (Dashboard component mount)
 *    - Only if it's the user's first time
 *    - Or when user explicitly asks for onboarding help
 *
 * 5. **Caching**
 *    - These endpoints can be called immediately
 *    - No rate limiting concerns
 *    - Results can be cached for the session
 */
